import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart3, FiTrendingUp, FiUsers, FiEye, FiExternalLink } = FiIcons;

const LinkedInAnalytics = () => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const analyticsStats = [
    { icon: FiEye, number: '192,313', label: 'Total LinkedIn Impressions', color: 'text-blue-400' },
  
    { icon: FiUsers, number: '74,683', label: 'Unique LI Members Reached', color: 'text-purple-400' },
    
  ];

  return (
    <motion.div
      className="glass-effect rounded-2xl p-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-center mb-8">
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <SafeIcon icon={FiBarChart3} className="text-blue-400 mr-3" />
          LinkedIn Analytics - Verified Performance Data
        </motion.h2>
        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Analytics Screenshot */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="glass-effect rounded-xl p-4 border border-blue-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center">
                <SafeIcon icon={FiBarChart3} className="text-blue-400 mr-2" />
                Official LinkedIn Analytics Dashboard
              </h3>
            </div>
            
            <div className="relative rounded-lg overflow-hidden bg-white/5">
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758202895687-image.png"
                alt="LinkedIn Analytics showing 192,313 impressions and 74,683 members reached"
                className={`w-full h-auto transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  e.target.style.display = 'none';
                  setImageLoaded(false);
                }}
              />
              {!imageLoaded && (
                <div className="w-full h-64 bg-gradient-to-br from-blue-600/30 to-purple-600/30 flex items-center justify-center">
                  <div className="text-center">
                    <SafeIcon icon={FiBarChart3} className="text-blue-400 text-4xl mx-auto mb-2" />
                    <p className="text-white/80">Loading analytics data...</p>
                  </div>
                </div>
              )}
              
              {/* Verification badge overlay */}
              <div className="absolute top-3 right-3 bg-green-500/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-1">
                <SafeIcon icon={FiBarChart3} className="text-white text-sm" />
                <span className="text-white text-xs font-semibold">Verified</span>
              </div>
            </div>
            
            <p className="text-white/70 text-sm mt-3 flex items-center">
              <SafeIcon icon={FiExternalLink} className="text-blue-400 mr-2" />
              Direct screenshot from ProActuary's AI Actuary Campaign LinkedIn analytics dashboard
            </p>
          </div>
        </motion.div>

        {/* Key Metrics Breakdown */}
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="text-green-400 mr-2" />
            Verified Performance Metrics
          </h3>
          
          {analyticsStats.map((stat, index) => (
            <motion.div
              key={index}
              className="glass-effect rounded-xl p-4 border border-white/10 hover:border-white/30 transition-colors"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={stat.icon} className={`${stat.color} text-xl`} />
                  <div>
                    <div className="text-white font-semibold">{stat.label}</div>
                    <div className="text-white/70 text-sm">ProActuary AI Actuary Campaign LinkedIn Analytics</div>
                  </div>
                </div>
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.number}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl p-6 mt-8 border border-blue-500/30"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8 }}
      >
        <h3 className="text-xl font-bold text-white mb-3 flex items-center">
          <SafeIcon icon={FiBarChart3} className="text-blue-400 mr-2" />
          Data Transparency
        </h3>
        <p className="text-white/90 leading-relaxed">
          These numbers aren't estimates - they're directly from LinkedIn's official analytics platform via ProActuary's CEO marketing account. The <span className="font-bold text-blue-300">192,313 impressions</span> and 
          <span className="font-bold text-purple-300"> 74,683 unique members reached</span> represent 
          verified, trackable brand exposure across LinkedIn's professional network during our 3-week campaign period.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default LinkedInAnalytics;